import { Component, ElementRef, EventEmitter, HostBinding, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, QueryList, SimpleChanges, ViewChildren } from "@angular/core";
import { VVehicleVideoStreamComponent } from "@app/directives/_vehicle/VehicleVideoStream/v-vehicle-video-stream.component";
import { CameraLocation } from "@app/main/components/videos/enums/CameraLocation";
import { VideoStreamType } from "@app/main/components/videos/enums/VideoStreamType";
import { FtCloudStreamType, ICameraChannel, IVideoStream } from "@app/main/components/videos/models/ICameraChannel";
import { IVideoAccelerometerData } from "@app/main/components/videos/models/IVideoAccelerometerData";
import { CameraService } from "@app/main/components/videos/services/camera.service";
import { ConstantsService } from "@app/services/constantsService";
import { DateService, DateUnit } from "@app/services/date.service";
import { FullScreenService } from "@app/services/fullscreen.service";
import { animFadeInOut } from "@app/styles/animations/fade-in-fade-out.animation";
import { UntilDestroy } from "@ngneat/until-destroy";
import { Subject } from "rxjs";
import { VideoStreamService } from "./stream.service";
import './v-stream-collection.component.scss';

@UntilDestroy()
@Component({
    selector: 'v-stream-collection',
    template: require('./v-stream-collection.component.html'),
    animations: [animFadeInOut]
})
export class VStreamCollectionComponent implements OnInit, OnChanges, OnDestroy {
    @Input() channels: IVideoStream[];
    @Input() highQuality: boolean;
    @Input() vehicleId: number;
    @Input() availableStreamQualities: VideoStreamType[];
    @Input() timeoutOnMouseIdle: boolean;
    @Input() streamTime: Date;
    @Input() isPreview = true;
    @Input() hideSkipControls = false;

    @Output() videoPlayStateChange = new EventEmitter<boolean>();
    @Output() videoTimedOut = new EventEmitter<void>();
    @Output() videoMediaReceived = new EventEmitter<any>();
    @Output() cameraUnavailable = new EventEmitter<any>();
    @Output() restartStreams = new EventEmitter<any>();
    @Output() timeChanged = new EventEmitter<Date>();

    @ViewChildren('videoStream') vStreamComponents: QueryList<VVehicleVideoStreamComponent>;
    @HostListener('mousemove', ['$event'])

    mousemove($event): void {
        this.streamService.resetStreamTimeout();
    }

    //--

    allPreviewStreams: IVideoStream[];
    playbackStreams: IVideoStream[];

    streamUpdated = new Subject<IVideoStream>();

    fitToFrame = false;
    isZoomed = false;

    showPlaybackSpeedOptions = false;
    vertical = false;
    fullscreen = false;
    ignore = false;

    isPlaying = false;
    firstPlaybackStarted: boolean;
    videoErrored: boolean;
    videoHasTimedOut: boolean;

    cameraChannels: ICameraChannel[];

    currentAccelerometerData: IVideoAccelerometerData;

    CameraLocation = CameraLocation;

    @HostBinding('class.is-mobile') isMobile: boolean;

    rafNumber: number;
    lastMedianPlayTime: number;
    animateCallCount = 0;
    actualAnimationCount = 0;

    showVideoControls: boolean;

    previewStreamType: FtCloudStreamType;

    controlsTimeout: ReturnType<typeof setTimeout>;
    ignoreTimeout: ReturnType<typeof setTimeout>;

    loadingStreams: boolean;

    constructor(
        private readonly el: ElementRef,
        private readonly constantsService: ConstantsService,
        private readonly cameraService: CameraService,
        private readonly dateService: DateService,
        private readonly fullScreenService: FullScreenService,
        private readonly streamService: VideoStreamService) { }

    ngOnDestroy(): void {
        this.clearAllTimeouts();
    }

    ngOnInit(): void {
        this.initialise();
    }

    ngOnChanges(changes: SimpleChanges): void {
        
        if (changes['channels']) {
            this.setVisibleChannelsForLiveView();
            this.setMainChannel();
            this.matchChannelsToStreams();
        }

        if (changes['channels'] && !changes['channels'].firstChange && this.isPlaying && this.channelAdded(changes['channels'].previousValue, changes['channels'].currentValue) && this.isPreview) {
            this.restart();
        }

        if (this.playbackStreams && changes["highQuality"] && changes["highQuality"].currentValue != changes["highQuality"].previousValue) {
            this.updateStreamQuality();
        }

        if (changes['streamTime']) {
            this.seekPreview();
        }
    }

    showFullscreen(): void {
        this.fullscreen = this.fullScreenService.toggle(this.el.nativeElement);
    }

    onVideoTimedOut(): void {
        this.videoHasTimedOut = true;
        this.videoTimedOut.emit();
    }

    onVideoReceivedMedia(data: any): void {
        this.videoErrored = false;
        this.showVideoControls = true;
        this.videoMediaReceived.emit(data);
        this.emitIsPlaying(this.isPlaying);
    }

    onCameraUnavailable(camera: IVideoStream): void {
        this.playbackStreams.find(stream => stream == camera).errored = true;

        const remainingStreams = this.playbackStreams.filter(stream => {
            return stream !== camera && !stream.errored;
        });

        if (!remainingStreams.length) {
            this.videoErrored = true;
        }

        this.cameraUnavailable.emit(camera);
    }

    getNewUrl(channel: IVideoStream): void {
        if (!this.vehicleId) {
            throw new Error("No VehicleId provided to component.");
        }

        const streamType = this.highQuality
            ? FtCloudStreamType.Mainstream
            : FtCloudStreamType.Substream;

        this.cameraService.getLiveStreamUrls(this.vehicleId, { StreamType: streamType })
            .then((channels) => {
                this.streamUpdated.next(channels.find(c => c.ChannelId == channel.ChannelId));
            })
    }

    focusStream(channel: IVideoStream): void {
        this.playbackStreams.forEach(c => {
            c.focused = false;
        });

        channel.focused = true;
    }

    emitIsPlaying(isPlaying: boolean): void {
        this.firstPlaybackStarted = true;
        this.isPlaying = isPlaying;

        this.videoPlayStateChange.emit(isPlaying);
    }

    startPreviewClicked(): void {
        if (this.allPreviewStreams?.length || !this.isPreview) {
            this.play();
            return;
        }

        this.getHistoricalStreamUrls();
    }

    play(): void {
        if (!this.vStreamComponents || this.ignore) {
            return;
        }

        this.vStreamComponents.toArray().forEach(video => {
            video.play();
        });

        this.isPlaying = true;
        this.setIgnore();
    }

    pause(): void {
        if (!this.vStreamComponents) {
            return;
        }

        let playerExists = false;

        this.vStreamComponents.toArray().forEach(video => {
            if (!video.flvPlayer.player) {
                return;
            }

            playerExists = true;
            video.flvPlayer.player.pause();
        });

        if (!playerExists) {
            return;
        }

        this.isPlaying = false;
        this.setIgnore();
    }

    async skip(time: number): Promise<void> {
        if (!this.playbackStreams?.length || this.ignore) {
            return;
        }

        this.showVideoControls = false;

        this.setIgnore(2000);

        this.streamTime = this.dateService.addOrSubtractTime(this.streamTime, time, DateUnit.Sec);

        this.timeChanged.emit(this.streamTime);
    }

    restart(): void {
        this.videoErrored = false;
        this.playbackStreams.forEach(stream => stream.errored = false);
        this.showVideoControls = false;
        this.playbackStreams = [];
        this.getHistoricalStreamUrls();
        this.restartStreams.emit();
    }

    // ---

    private channelAdded(previousCameraChannels: ICameraChannel[], currentCameraChannels: ICameraChannel[]): boolean {
        return previousCameraChannels.length < currentCameraChannels.length;
    }

    private async seekPreview(): Promise<IVideoStream[]> {
        if (!this.playbackStreams?.length) {
            return;
        }

        await this.restart();
    }

    private getHistoricalStreamUrls(): void {
        this.videoErrored = false;

        if (!this.isPreview) {
            return;
        }

        this.loadingStreams = true;

        this.previewStreamType = (this.availableStreamQualities?.includes(VideoStreamType.Sub))
            ? FtCloudStreamType.Substream
            : FtCloudStreamType.Mainstream;

        this.cameraService.getPlaybackUrls(this.vehicleId, { Length: 120 * 60, Start: this.streamTime, StreamType: this.previewStreamType }).then(streams => {
            if (!streams?.length) {
                console.error("No streams found for vehicle");
                return;
            }

            if (!this.channels?.length) {
                console.error("No active channels found for vehicle", this.channels);
                return;
            }

            this.allPreviewStreams = this.cameraService.filterDuplicateVideoStreams(streams).filter(stream => stream.IsActive);

            this.matchChannelsToStreams()
            this.setMainChannel();

            this.play();
            this.loadingStreams = false;
        });
    }

    private matchChannelsToStreams() {
        if (!this.allPreviewStreams?.length || !this.isPreview) {
            return;
        }

        this.playbackStreams = [];

        this.channels.forEach(channel => {
            const matchingStream = this.allPreviewStreams.find(s => s.ChannelId == channel.ChannelId)

            if (!matchingStream) {
                return;
            }

            channel.loaded = true;
            channel.StreamUrl = matchingStream.StreamUrl;
            channel.PlaybackSession = matchingStream.PlaybackSession;
            this.playbackStreams.push(channel);
        });
    }

    private setIgnore(timeout = 10): void {
        this.ignore = true;

        this.ignoreTimeout = setTimeout(() => {
            this.ignore = false;
        }, timeout);
    }

    private initialise(): void {
        this.isMobile = this.constantsService.getIsMobile();

        // This allows us to ensure we have been created for at least 300ms before we try to play
        setTimeout(() => {
            this.vertical = this.el.nativeElement.offsetWidth / this.el.nativeElement.offsetHeight < 1.4;
            if (!this.isPreview) {
                this.setVisibleChannelsForLiveView();
                this.play();
            }
        }, 300);
    }

    private setMainChannel(): void {
        if (!this.playbackStreams?.length) {
            return;
        }

        const primaryChannel = this.cameraService.getPrimaryChannel(this.playbackStreams);

        if (primaryChannel) {
            this.focusStream(primaryChannel);
        }
        else {
            //Sets a main channel if none is found in the selection when viewing multiple channels eg. viewing Internal and Nearside but not Forward
            this.focusStream(this.playbackStreams[0]);
        }
    }

    private updateStreamQuality(): void {
        this.vStreamComponents.forEach(videoStream => {
            videoStream.toggleHighQuality(this.highQuality);
        });
    }

    private setVisibleChannelsForLiveView(): void {
        if (this.isPreview) {
            return;
        }

        this.playbackStreams = this.channels;
    }

    private clearAllTimeouts(): void {
        clearTimeout(this.controlsTimeout);
        clearTimeout(this.ignoreTimeout);
    }
}
